import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Form, Formik, FormikProps } from 'formik'
import { useSnackbar } from 'notistack'
import { useDispatch, useSelector } from 'react-redux'
import { Typography } from '@mui/material'
import { StatusChip } from 'src/components/payments-ops/StatusChip'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { ConfirmActionModal } from 'src/components/shared/ConfirmActionModal'
import { DataRow } from 'src/components/shared/DataRow'
import { FormikTextArea } from 'src/components/shared/form/formik/FormikTextArea'
import { AdminFetchPendingPaymentDetails, AdminReviewPayment } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { PaymentReviewStatus } from 'src/constants/payments-ops/enums'
import { paymentReviewReasonLabels } from 'src/constants/payments-ops/labels'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { PendingPayment, ReviewDecision } from 'src/interfaces/paymentsReview'
import {
  getPaymentReviewSubmitting,
  getPendingPaymentDetails,
  getPendingPaymentDetailsError,
  getPendingPaymentDetailsLoading,
} from 'src/selectors/paymentsReview'
import { formatDateTime, parseUnixTimestamp } from 'src/utils/grid'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { formatDriverName } from 'src/utils/paymentsOps/pendingPayments'
import { OtherEarningsTable } from './OtherEarningsTable'
import { reviewPaymentValidator } from './ReviewPaymentModal.validator'

interface Props {
  /** `null` closes the modal. */
  payment: PendingPayment | null
  onClose(): void
}

interface ReviewPaymentFormValues {
  reviewNotes: string
}

const INITIAL_VALUES: ReviewPaymentFormValues = { reviewNotes: '' }

const submitNothing = () => {
  return undefined
}

const ReviewPaymentModalComponent = ({ payment, onClose }: Props) => {
  const dispatch = useDispatch()
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const details = useSelector(getPendingPaymentDetails)
  const isDetailsLoading = useSelector(getPendingPaymentDetailsLoading)
  const detailsError = useSelector(getPendingPaymentDetailsError)
  const isSubmitting = useSelector(getPaymentReviewSubmitting)
  // Without `payments_review_decide` the modal is a read-only view of the payment and the driver's
  // other earnings: no review note, no Approve / Reject (spec §7.5).
  const canDecide = useHasPermission(InternalPermission.PaymentsReviewDecide)

  const [pendingDecision, setPendingDecision] = useState<ReviewDecision | null>(null)
  const [confirmedNotes, setConfirmedNotes] = useState<string>('')

  const earningId = payment?.id ?? null

  useEffect(() => {
    if (!earningId) {
      return
    }

    // Plain dispatch: the modal renders the error inline (a missing earning is a backend 500).
    dispatch(AdminFetchPendingPaymentDetails.request({ earningId }))
  }, [dispatch, earningId])

  // Details for a DIFFERENT earning must never be shown while the right one loads.
  const otherEarnings = useMemo(() => {
    if (!details || details.id !== earningId) {
      return []
    }

    return details.allOtherEarnings
  }, [details, earningId])

  const requestDecision = useCallback(
    async (formik: FormikProps<ReviewPaymentFormValues>, decision: ReviewDecision) => {
      const errors = await formik.validateForm()

      formik.setTouched({ reviewNotes: true })

      if (Object.keys(errors).length > 0) {
        return
      }

      setConfirmedNotes(formik.values.reviewNotes.trim())
      setPendingDecision(decision)
    },
    [],
  )

  // The review write is in flight: no path (Cancel, backdrop, Escape) may dismiss either modal
  // until it settles, otherwise the outcome would land on a closed modal.
  const handleClose = useCallback(() => {
    if (isSubmitting) {
      return
    }

    onClose()
  }, [isSubmitting, onClose])

  const handleCancelDecision = useCallback(() => {
    if (isSubmitting) {
      return
    }

    setPendingDecision(null)
  }, [isSubmitting])

  const handleConfirmDecision = useCallback(async () => {
    if (!payment || !pendingDecision) {
      return
    }

    const isApproveDecision = pendingDecision === PaymentReviewStatus.ManuallyApproved

    // A failed approve gets one explicit warning instead of the generic error toast: the backend
    // can 500 after the approval was saved, and its message ("Internal server error") says nothing.
    const result = await asyncDispatchWithErrorToast(
      AdminReviewPayment.request({
        earningId: payment.id,
        reviewStatus: pendingDecision,
        reviewNotes: confirmedNotes,
      }),
      isApproveDecision,
    )

    setPendingDecision(null)

    if (!result) {
      if (isApproveDecision) {
        snackbar?.enqueueSnackbar('Approval may have been saved — refresh before retrying.', {
          variant: 'warning',
        })
      }

      // Never leave Approve/Reject live on a payment whose state is now unknown: the operator
      // must reopen it from the refetched queue (a reject after a saved approve would refund it).
      onClose()

      return
    }

    snackbar?.enqueueSnackbar(`Payment ${isApproveDecision ? 'approved' : 'rejected'}`, {
      variant: 'success',
    })
    onClose()
  }, [asyncDispatchWithErrorToast, confirmedNotes, onClose, payment, pendingDecision, snackbar])

  if (!payment) {
    return null
  }

  const amount = formatCentsAsUsd(payment.amount)
  const driverName = formatDriverName(payment.partnerDriverName)
  const reason = payment.reviewReason ? paymentReviewReasonLabels[payment.reviewReason] : null
  const isApprove = pendingDecision === PaymentReviewStatus.ManuallyApproved

  return (
    <Formik<ReviewPaymentFormValues>
      initialValues={INITIAL_VALUES}
      validationSchema={reviewPaymentValidator}
      onSubmit={submitNothing}
    >
      {(formik) => {
        return (
          <Fragment>
            <CommonModal
              isOpen={Boolean(payment)}
              title="Review payment"
              subtext={`${payment.partner} · ${driverName}`}
              // Wide enough for the other-earnings grid's columns (~770px), so it never scrolls
              // sideways.
              className={tw`max-w-[920px]`}
              onClose={handleClose}
              buttons={
                <Fragment>
                  {canDecide ? (
                    <Button
                      size="small"
                      color="red"
                      disabled={isSubmitting}
                      onClick={() => {
                        requestDecision(formik, PaymentReviewStatus.Rejected)
                      }}
                      data-testid="review-payment-reject"
                    >
                      Reject…
                    </Button>
                  ) : null}
                  <Button size="small" color="grey" disabled={isSubmitting} onClick={handleClose}>
                    Cancel
                  </Button>
                  {canDecide ? (
                    <Button
                      size="small"
                      color="dark"
                      disabled={isSubmitting}
                      onClick={() => {
                        requestDecision(formik, PaymentReviewStatus.ManuallyApproved)
                      }}
                      data-testid="review-payment-approve"
                    >
                      Approve…
                    </Button>
                  ) : null}
                </Fragment>
              }
            >
              <Form css={tw`flex flex-col gap-4`}>
                <div css={tw`flex flex-col gap-2 rounded-lg bg-shades-50 p-4`}>
                  <DataRow title="Partner" text={payment.partner} />
                  <DataRow title="Driver" text={driverName} />
                  <DataRow title="Email" text={payment.partnerDriverEmail} />
                  <DataRow title="Phone" text={payment.partnerDriverPhone} />
                  <DataRow title="Amount" text={amount} />
                  <DataRow
                    title="Review reason"
                    component={
                      reason ? <StatusChip label={reason.label} tone={reason.tone} /> : undefined
                    }
                    text={reason ? undefined : '—'}
                  />
                  <DataRow
                    title="Created at"
                    text={formatDateTime(parseUnixTimestamp(payment.createdAt))}
                  />
                  <DataRow title="Payment ID" text={payment.id} direction="column" />
                </div>
                <div css={tw`flex flex-col gap-2`}>
                  <Typography variant="h6">Driver&apos;s other earnings</Typography>
                  {detailsError ? (
                    <Typography variant="body2" css={tw`text-critical-500`}>
                      {detailsError}
                    </Typography>
                  ) : (
                    <OtherEarningsTable earnings={otherEarnings} isLoading={isDetailsLoading} />
                  )}
                </div>
                {canDecide ? (
                  <FormikTextArea<ReviewPaymentFormValues>
                    name="reviewNotes"
                    label="Review note *"
                    placeholder="Why is this being approved or rejected?"
                    minRows={3}
                    fullWidth
                    data-testid="review-payment-notes"
                  />
                ) : null}
              </Form>
            </CommonModal>
            <ConfirmActionModal
              isOpen={pendingDecision !== null}
              title={isApprove ? 'Approve payment?' : 'Reject payment?'}
              description={
                isApprove
                  ? `Approve ${amount} for ${driverName} (${payment.partner})? This releases the payment and cannot be undone.`
                  : `Reject the ${amount} payment for ${driverName}? The payment is marked failed and a refund is created in Refunds → Created.`
              }
              details={
                <Typography variant="body2" css={tw`text-xs text-shades-500`}>
                  Note: “{confirmedNotes}”
                </Typography>
              }
              confirmLabel={isApprove ? `Approve ${amount}` : 'Reject payment'}
              isDestructive={!isApprove}
              isSubmitting={isSubmitting}
              onConfirm={handleConfirmDecision}
              onClose={handleCancelDecision}
              confirmDataTestId="review-payment-confirm"
            />
          </Fragment>
        )
      }}
    </Formik>
  )
}

export const ReviewPaymentModal = memo(ReviewPaymentModalComponent)
